<template>
    <div 
        class="form__item" 
        :class="[
            wrapperClass, 
            noMarginBottom ? 'mb--0' : '', 
            border === 'wrap' ? 'form-control form__input form-control-md form-control-border-solid form-control-bg-white' : ''
        ]" 
    >
        <FormLabel
            v-if="label" 
            :label-type="labelType"
            :info-type="infoType" 
            :custom-text="customText"
            :html-for="id" 
            :title="label"
        />

        <template v-if="!readonly">
            <div :class="{ 'form__input-group': isPrefix || isSuffix || loading || addOnText, 'input-group': addOnText }">
                <span v-if="isPrefix" class="left-icon">
                    <slot name="prefix" :class="['form-input__prefix', prefixClass]" />                                      
                </span>
                
                <span v-if="addOnText" class="input-group-text">
                    <span id="basic-addon1" class="form-input__prefix text--xs">{{ addOnText }}</span>
                </span>
    
                <input 
                    v-bind="{...$attrs, type: computedType }" 
                    ref="inputElement"
                    class="form-control form__input"
                    :class="
                        {
                            'prefix': isPrefix,
                            'form__input-error': isError,
                            [`form-control-${size}`]: size,
                            [`form-control-border-${border}`]: border,
                            [`form-control-bg-${bg}`]: bg,
                            'form__input__border__none': border === 'none' || border === 'wrap',
                        }"
                    :value="modelValue"
                    :autocomplete="id"
                    @input="emitValue"
                    @change="e => $emit('change', e)"
                    @focus="onFocus"
                    @blur="onBlur"
                    @keydown="e => $emit('keydown', e)"
                    @keyup="handleKeyUp"
                >
    
                <template v-if="loading">
                    <PageSpinner 
                        class="mr--5 base_btn w--fit"
                        size="sm" 
                        :is-page="false" 
                    />
                </template>
    
                <template v-else>
                    <span v-if="isSuffix" class="input--password absolute me-1 cursor-pointer">
                        <slot name="suffix" :class="['form-input__suffix', suffixClass]" />
                        <template v-if="field === 'password'">
                            <span @click="toggleViewPasswordIcon">
                                <icon-svg fill="rgba(194, 200, 212, 1)" :name="showPassword ? 'show' : 'eye-hide'" :width="'16px'" :height="'16px'" /> 
                            </span>
                        </template>                  
                    </span>
                </template>
            </div>
        </template>
        <template v-else>
            <span
                class="text-gray-600 form-control"
                :class="[`text--${size}`, `form-control-${size}`, `${readonly ? 'disabled': ''}`]"
            >
                {{ modelValue }}
            </span>
        </template>
        <p v-if="isError" class="form__err-msg">
            {{ errMsg }}
        </p>
        
        <p v-if="!isError && helpMsg" class="form-text text--xs">
            {{ helpMsg }}
        </p>
    </div>
</template>

<script>
import FormLabel from "../forms/FormLabel.vue";
import IconSvg from "../icons/Icon-Svg.vue";
import PageSpinner from "../loaders/PageSpinner.vue";

export default {
    name: "FormInput",

    components: {
        FormLabel,
        IconSvg,
        PageSpinner,
    },

    inheritAttrs: false,
    
    model: {
        prop: "value",
        event: "input",
    },

    props: {
        modelValue: { default: "", type: [Number, String] },
        modelModifiers: { type: Object, default: () => ({}) },
        label: { default: "", type: String },
        addOnText: { default: "", type: String },
        readonly: { type: Boolean },
        loading: { type: Boolean, default: false },
        disabled: { type: Boolean, default: false },
        isPrefix: { type: Boolean, default: false },
        isSuffix: { type: Boolean, default: false },
        labelType: {
            type: String,
            default: "form",
            validator: value => !value || [
                "marketing", "title", "menu", "md_mono", "tabular", "form", "tertiary", "sm_mono",
            ].includes(value),
        },
        type: {
            default: "text",
            type: String,
            validator: (value) => {
                return [
                    "date",
                    "email",
                    "number",
                    "password",
                    "search",
                    "tel",
                    "text",
                ].includes(value);
            },
        },
        id: {
            type: String,
            default: "",
        },

        // Use this to minimize the effect of the @input event to make it trigger only after you're done typing.
        // This offers a performance boost to the Input element as it seems faster cause of less back and forth.
        lazy: {
            type: Boolean,
            default: false,
        },

        wrapperClass: {
            type: String,
            default: "",
        },

        required: {
            type: Boolean,
            default: undefined,
        },
        // Define a list of classes you want applied to your prefix
        prefixClass: {
            type: String,
            default: "",
        },
        // Define a list of classes you want applied to your suffix
        suffixClass: {
            type: String,
            default: "",
        },
        isError: {
            type: Boolean,
            default: false,
        },
        errMsg: {
            type: String,
            default: "",
        },
        infoType: {
            type: String,
            default: "",
        },
        customText: {
            type: Boolean,
            default: false,
        },
        size: {
            type: String,
            default: "sm",
            validator: (value) => {
                return ["sm", "md", "lg"].includes(value);
            },
        },
        bg: {
            type: String,
            default: "white",
            validator: (value) => {
                return ["gray", "white", ""].includes(value);
            },
        },
        border: {
            type: String,
            default: "solid",
            validator: (value) => {
                return ["solid", "none", "dotted", "rounded", "rounded-shadow"].includes(value);
            },
        },
        helpMsg: {
            type: String,
            default: "",
        },
        noMarginBottom: {
            type: Boolean,
            default: false,
        },
        field: {
            type: String,
            default: "",
        },
        inputType: {
            type: String,
            default: "normal",
            validator: (value) => {
                return ["normal", "debounced"].includes(value);
            },
        },
        autofocus: {
            type: Boolean,
            default: false,
        },
    },

    emits: ["input", "blur", "change", "keydown", "focus", "keyup", "update:modelValue"],

    data() {
        return {
            focusState: "blur", // for styling purposes
            showPassword: false,
        };
    },

    computed: {
        computedType() {
            if (this.field !== "password" && !this.type) return "text";

            if (this.field === "password") {
                if (!this.showPassword) return "password";
                if (this.showPassword) return "text";
            }

            return this.type;
        },
    },

    mounted() {       
        setTimeout(() => {
            if (this.autofocus) {                
                this.$refs.inputElement?.focus();
            }
        }, 200);
    },

    methods: {
        onFocus(e) {
            // Emitted when you focus on the input.
            this.focusState = "focus";
            this.$emit("focus", e);
            // this.focus();
        },

        onBlur(e) {
            // Emitted when you blur off the input.
            this.focusState = "blur";
            this.$emit("blur", e);
        },

        handleKeyUp(event) {
            this.$emit("keyup", event);
        },

        getInputElement() {
            return this.$refs.inputElement;
        },

        emitValue(e) {
            let value = e.target.value;
                        
            if (this.modelModifiers.capitalize) {
                value = value.charAt(0).toUpperCase() + value.slice(1);
            }

            if (this.inputType === "normal") {
                this.$emit("update:modelValue", value);
            } else {
                this.$emit("input", value);
            }
        },

        toggleViewPasswordIcon() {
            this.showPassword = !this.showPassword;
        },
    },
};
</script>